/**
 * "تصميم الروشتة" — pick the letterhead template and its colours.
 *
 * A port of the legacy designer (`doctor_dashboard.js:20010`), which offered
 * four templates and two colour pickers. The rebuild had the colour pickers but
 * no way to choose a template at all, so every doctor printed `wave-top`.
 *
 * Two things the legacy did not have:
 *   - **Ready-made palettes.** Two hex fields are a poor way to choose a colour
 *     scheme; most doctors want a good pair, not a colour wheel.
 *   - **A real preview.** The legacy thumbnails were hand-drawn SVG sketches
 *     that only approximated the output. "معاينة" here renders the actual print
 *     template with sample data, so what you approve is what prints.
 */

import { useMemo, useState } from 'react';
import { Button, Field, Icon, Input } from '../../components/common';
import { buildPrescriptionHtml, PRESCRIPTION_TEMPLATES } from '../../domain/prescriptionPrint';
import { printPrescription } from '../../services/printPrescription';
import { useNotifications } from '../../context/NotificationContext';
import { useDebounced } from '../../hooks/useLocalStorage';
import { useBranding } from '../../services/branding';
import { poweredByLine } from '../../domain/branding';

/**
 * Palettes that read well on paper.
 *
 * Every pair was checked as ink: the secondary is only ever used at low opacity
 * behind the primary, so it has to differ in hue rather than only in lightness
 * or the header prints as one flat band.
 */
export const COLOR_THEMES = [
  // Named after the platform on screen (the Super Admin may rename it); the id stays.
  { id: 'capsule', label: 'كبسولة', primary: '#06b6d4', secondary: '#a855f7' },
  { id: 'emerald', label: 'زمردي', primary: '#059669', secondary: '#14b8a6' },
  { id: 'royal', label: 'ملكي', primary: '#4f46e5', secondary: '#8b5cf6' },
  { id: 'rose', label: 'وردي', primary: '#e11d48', secondary: '#f97316' },
  { id: 'amber', label: 'عنبري', primary: '#d97706', secondary: '#eab308' },
  { id: 'ocean', label: 'محيطي', primary: '#0369a1', secondary: '#0891b2' },
  { id: 'plum', label: 'برقوقي', primary: '#7e22ce', secondary: '#db2777' },
  { id: 'graphite', label: 'رمادي', primary: '#334155', secondary: '#64748b' },
];

/**
 * A4 at 96dpi, and how far down to scale it for the panel.
 *
 * The iframe renders at true size — a print stylesheet laid out at 300px wide
 * reflows and stops resembling the output — and is then scaled visually, so
 * what is on screen is the real sheet, just smaller.
 */
const PREVIEW_WIDTH = 794;
const PREVIEW_HEIGHT = 1123;
// 0.45 puts a whole A4 sheet inside the modal at once. Larger looked better
// but cut the page off, which defeats the point of previewing a layout.
const PREVIEW_SCALE = 0.45;

/** A sample prescription, so the preview shows a full sheet rather than an empty one. */
const SAMPLE = {
  id: 'preview',
  date: new Date().toISOString().slice(0, 10),
  patientName: 'اسم المريض',
  diagnosis: 'التهاب في الجيوب الأنفية',
  medicines: [
    {
      name: 'أموكسيسيلين',
      dosage: '500 مجم',
      frequency: 'كل 8 ساعات',
      timing: 'بعد الأكل',
      duration: '7 أيام',
      notes: 'يؤخذ مع كوب ماء',
    },
    {
      name: 'باراسيتامول',
      dosage: '500 مجم',
      frequency: 'عند اللزوم',
      timing: '—',
      duration: '5 أيام',
      notes: '',
    },
  ],
  labTests: [{ name: 'صورة دم كاملة' }],
  xrays: [{ name: 'أشعة على الجيوب الأنفية' }],
  followUpDate: '2026-09-15T18:00',
  notes: 'راحة تامة وشرب سوائل دافئة.',
};

export function PrescriptionDesigner({ form, setForm, doctorProfile }) {
  const brand = useBranding();
  const paletteLabel = (theme) => (theme.id === 'capsule' ? brand.name || 'الأساسي' : theme.label);
  const { error: toastError } = useNotifications();
  const [previewing, setPreviewing] = useState(false);

  const primary = form.printPrimaryColor;
  const secondary = form.printSecondaryColor;
  const selected = form.prescriptionStyle;

  const applyTheme = (theme) =>
    setForm((current) => ({
      ...current,
      printPrimaryColor: theme.primary,
      printSecondaryColor: theme.secondary,
    }));

  const setColor = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Preview the settings as they are on screen, not as they were last
      // saved — otherwise "معاينة" would show the previous design.
      await printPrescription({
        prescription: SAMPLE,
        doctor: {
          ...doctorProfile,
          logo: form.logo,
          prescriptionStyle: selected,
          printColors: { primary, secondary },
        },
        patientName: SAMPLE.patientName,
        patientCode: 'P-000',
      });
    } catch (err) {
      toastError(err.message || 'تعذر فتح المعاينة');
    } finally {
      setPreviewing(false);
    }
  };

  /**
   * The preview, as an actual rendered sheet.
   *
   * Debounced because the colour inputs fire continuously while dragging, and
   * rebuilding a full A4 document on every pixel of a colour picker makes the
   * whole panel stutter. 180ms is below the point where it stops feeling live.
   */
  const debouncedPrimary = useDebounced(primary, 180);
  const debouncedSecondary = useDebounced(secondary, 180);

  const previewHtml = useMemo(() => {
    try {
      return buildPrescriptionHtml({
        prescription: SAMPLE,
        doctor: {
          ...doctorProfile,
          logo: form.logo,
          prescriptionStyle: selected,
          printColors: { primary: debouncedPrimary, secondary: debouncedSecondary },
        },
        patientName: SAMPLE.patientName,
        patientCode: 'P-000',
        poweredBy: poweredByLine(brand),
      });
    } catch {
      // A profile that has not loaded yet is not an error worth showing —
      // the panel simply says it is preparing.
      return null;
    }
  }, [doctorProfile, form.logo, selected, debouncedPrimary, debouncedSecondary, brand]);

  const legacy = PRESCRIPTION_TEMPLATES.filter((t) => t.group === 'legacy');
  const added = PRESCRIPTION_TEMPLATES.filter((t) => t.group !== 'legacy');

  return (
    <div className="space-y-5">
      {/* --- palettes --- */}
      <div>
        <p className="mb-2 text-sm font-bold text-slate-200">مجموعات ألوان جاهزة</p>
        <div className="flex flex-wrap gap-2">
          {COLOR_THEMES.map((theme) => {
            const active =
              theme.primary.toLowerCase() === String(primary).toLowerCase() &&
              theme.secondary.toLowerCase() === String(secondary).toLowerCase();
            return (
              <button
                key={theme.id}
                type="button"
                onClick={() => applyTheme(theme)}
                title={paletteLabel(theme)}
                aria-pressed={active}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-all ${
                  active
                    ? 'border-cyan-500 bg-cyan-500/10 text-cyan-200'
                    : 'border-white/10 bg-slate-900 text-slate-300 hover:border-white/25'
                }`}
              >
                <span className="flex">
                  <span
                    className="h-4 w-4 rounded-full ring-1 ring-black/30"
                    style={{ background: theme.primary }}
                  />
                  <span
                    className="-mr-1.5 h-4 w-4 rounded-full ring-1 ring-black/30"
                    style={{ background: theme.secondary }}
                  />
                </span>
                {paletteLabel(theme)}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- exact colours --- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ColorField label="اللون الأساسي" value={primary} onChange={setColor('printPrimaryColor')} />
        <ColorField
          label="اللون الثانوي"
          value={secondary}
          onChange={setColor('printSecondaryColor')}
        />
      </div>

      {/* --- templates --- */}
      <div>
        <p className="mb-2 text-sm font-bold text-slate-200">شكل الترويسة</p>

        <p className="mb-2 text-[11px] text-slate-500">التصاميم الأصلية</p>
        <TemplateRow
          templates={legacy}
          selected={selected}
          primary={primary}
          secondary={secondary}
          onSelect={(id) => setForm((c) => ({ ...c, prescriptionStyle: id }))}
        />

        <p className="mb-2 mt-4 text-[11px] text-slate-500">تصاميم جديدة</p>
        <TemplateRow
          templates={added}
          selected={selected}
          primary={primary}
          secondary={secondary}
          onSelect={(id) => setForm((c) => ({ ...c, prescriptionStyle: id }))}
        />
      </div>

      {/* The preview is the point of this screen, so it is always on. The
          legacy designer showed the sheet beside the picker and updated it as
          you chose; asking someone to press "معاينة" after every click made
          comparing two designs a chore. */}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-bold text-slate-200">
            <Icon name="eye" size={15} className="text-cyan-400" />
            معاينة مباشرة
          </p>
          <Button variant="subtle" icon="printer" loading={previewing} onClick={handlePreview}>
            فتح للطباعة
          </Button>
        </div>

        <div className="flex justify-center rounded-xl border border-white/10 bg-slate-950 p-3">
          {previewHtml ? (
            /*
             * dir="ltr" and absolute positioning are both load-bearing.
             *
             * The app is RTL, so an inline iframe is laid out from the right
             * edge of its line box. Scaling that from `top left` moved the
             * sheet out of its own window and left a sliver visible. Taking it
             * out of the inline flow removes the direction from the equation
             * entirely, so the origin means what it says.
             */
            <div
              dir="ltr"
              className="relative overflow-hidden rounded-lg shadow-lg shadow-black/40 ring-1 ring-white/10"
              style={{
                width: Math.round(PREVIEW_WIDTH * PREVIEW_SCALE),
                height: Math.round(PREVIEW_HEIGHT * PREVIEW_SCALE),
              }}
            >
              {/* An iframe rather than injected markup: the print document
                  carries its own full stylesheet, and letting that loose in the
                  app would restyle the page around it. */}
              <iframe
                title="معاينة الروشتة"
                srcDoc={previewHtml}
                sandbox=""
                scrolling="no"
                className="absolute left-0 top-0 border-0 bg-white"
                style={{
                  width: PREVIEW_WIDTH,
                  height: PREVIEW_HEIGHT,
                  transform: `scale(${PREVIEW_SCALE})`,
                  transformOrigin: 'top left',
                  pointerEvents: 'none',
                }}
              />
            </div>
          ) : (
            <p className="py-10 text-center text-xs text-slate-500">جاري تجهيز المعاينة…</p>
          )}
        </div>

        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          المعاينة ببيانات تجريبية وتتحدث فوراً مع كل تغيير. «فتح للطباعة» يفتح نافذة الطباعة
          لحفظها PDF. لن يتم حفظ التصميم حتى تضغط «حفظ».
        </p>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={onChange}
          className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-white/10 bg-transparent"
          aria-label={label}
        />
        <Input value={value} onChange={onChange} dir="ltr" />
      </div>
    </Field>
  );
}

function TemplateRow({ templates, selected, primary, secondary, onSelect }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {templates.map((template) => {
        const active = selected === template.id;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template.id)}
            aria-pressed={active}
            className="overflow-hidden rounded-xl border-2 transition-all"
            style={{
              borderColor: active ? primary : 'rgba(255,255,255,0.1)',
              boxShadow: active ? `0 0 14px ${primary}66` : 'none',
            }}
          >
            <div className="bg-white">
              <Thumbnail id={template.id} primary={primary} secondary={secondary} />
            </div>
            <div className="bg-black/40 px-1 py-1.5">
              <p
                className="text-center text-[10px]"
                style={{ color: active ? primary : '#64748b', fontWeight: active ? 700 : 400 }}
              >
                {template.label}
              </p>
              {active && (
                <p className="mt-0.5 flex items-center justify-center gap-1 text-[9px] text-emerald-400">
                  <Icon name="check" size={9} />
                  مُختار
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * A scaled sketch of each header, drawn in the doctor's own colours.
 *
 * Deliberately a sketch and not an iframe of the real thing: eight live
 * previews would each load the print stylesheet and web fonts. The «معاينة»
 * button renders the real template for the one design being considered.
 */
function Thumbnail({ id, primary, secondary }) {
  const lines = (
    <>
      <rect x="4" y="46" width="48" height="3" rx="1.5" fill="#e2e8f0" />
      <rect x="4" y="54" width="36" height="2" rx="1" fill="#e2e8f0" />
      <rect x="4" y="60" width="42" height="2" rx="1" fill="#e2e8f0" />
    </>
  );

  const shapes = {
    'wave-top': (
      <>
        <path d="M0 0 H56 V26 Q42 36 28 28 Q14 20 0 30 Z" fill={primary} />
        <path d="M0 0 H56 V18 Q42 27 28 20 Q14 13 0 22 Z" fill={secondary} opacity=".35" />
      </>
    ),
    'wave-sides': (
      <>
        <path d="M0 0 L34 0 Q26 16 34 32 L0 32 Z" fill={primary} />
        <path d="M0 0 L30 0 Q22 16 30 32 L0 32 Z" fill={secondary} opacity=".3" />
      </>
    ),
    diagonal: (
      <>
        <rect width="56" height="32" fill={primary} />
        <path d="M56 0 L56 32 L34 32 L44 0 Z" fill="white" opacity=".14" />
        <path d="M56 0 L56 32 L28 32 L38 0 Z" fill={secondary} opacity=".25" />
      </>
    ),
    'wave-full': (
      <>
        <rect width="56" height="34" fill={primary} />
        <path d="M0 22 Q14 34 28 26 Q42 18 56 30 L56 34 L0 34 Z" fill={secondary} opacity=".4" />
        <circle cx="9" cy="8" r="6" fill="white" opacity=".12" />
      </>
    ),
    minimal: (
      <>
        <rect x="4" y="8" width="26" height="4" rx="2" fill="#cbd5e1" />
        <rect x="4" y="16" width="18" height="2.5" rx="1.25" fill="#e2e8f0" />
        <rect x="0" y="26" width="56" height="2.5" fill={primary} />
        <rect x="0" y="29" width="56" height="1.2" fill={secondary} opacity=".5" />
      </>
    ),
    classic: (
      <>
        <circle cx="28" cy="9" r="5" fill={primary} opacity=".3" />
        <rect x="17" y="17" width="22" height="3.5" rx="1.75" fill="#cbd5e1" />
        <rect x="0" y="26" width="56" height="1.4" fill={primary} />
        <rect x="0" y="29" width="56" height="1.4" fill={primary} />
        <rect x="0" y="32" width="56" height="0.8" fill={secondary} opacity=".5" />
      </>
    ),
    ribbon: (
      <>
        <rect x="8" y="4" width="48" height="26" fill={primary} />
        <path d="M56 30 L56 36 L50 30 Z" fill={secondary} />
        <rect x="12" y="12" width="22" height="3" rx="1.5" fill="white" opacity=".8" />
      </>
    ),
    arch: (
      <>
        <path d="M0 0 H56 V22 Q28 34 0 22 Z" fill={primary} />
        <path d="M0 0 H56 V16 Q28 28 0 16 Z" fill={secondary} opacity=".3" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 56 72" className="h-auto w-full" role="img" aria-hidden="true">
      <rect width="56" height="72" fill="white" />
      {shapes[id] || shapes['wave-top']}
      {lines}
      <rect x="0" y="66" width="56" height="6" fill={primary} opacity=".85" />
    </svg>
  );
}

export default PrescriptionDesigner;
